import { createClient } from '@supabase/supabase-js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import type { DiagnosticsData, DiagnosticsTest } from '../types/diagnostics'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Column mapping from legend.md to database fields
const COLUMN_MAPPING: Record<string, string> = {
  t: 'time',
  Af: 'breathing_frequency',
  VT: 'tidal_volume',
  VE: 'minute_ventilation',
  VO2: 'vo2',
  VCO2: 'vco2',
  R: 'respiratory_exchange_ratio',
  'VE/VO2': 've_vo2_ratio',
  'VE/VCO2': 've_vco2_ratio',
  METS: 'mets',
  HF: 'heart_rate',
  FeO2: 'feo2',
  FeCO2: 'feco2',
  FetO2: 'feto2',
  FetCO2: 'fetco2',
  FiO2: 'fio2',
  FiCO2: 'fico2',
  PeO2: 'peo2',
  PeCO2: 'peco2',
  PetO2: 'peto2',
  PetCO2: 'petco2',
  Steigung: 'treadmill_gradient',
  Geschwindigkeit: 'speed_kmh',
  Phase: 'test_phase',
  EEh: 'energy_expenditure_hour',
  EEm: 'energy_expenditure_minute',
  Pace: 'pace_min_per_km',
}

function parseNumericValue(value: any): number | null {
  if (value === null || value === undefined || value === '') return null

  // Handle string values
  if (typeof value === 'string') {
    // Remove any non-numeric characters except decimal point and negative sign
    const cleaned = value.replace(/[^\d.-]/g, '')
    const parsed = Number.parseFloat(cleaned)
    return Number.isNaN(parsed) ? null : parsed
  }

  // Handle numeric values
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value
  }

  return null
}

export interface DiagnosticsThresholds {
  vt1?: DiagnosticsData
  vt2?: DiagnosticsData
}

export interface DiagnosticsStats {
  weight: number | null
  VO2max: number | null
  maxVO2: number | null
  avgVO2: number | null
  maxHR: number | null
  vt1?: DiagnosticsData
  vt2?: DiagnosticsData
  totalTests: number | null
  latestTest: string | null
}

export function useDiagnostics(athleteId: string) {
  const [tests, setTests] = useState<DiagnosticsTest[]>([])
  const [data, setData] = useState<DiagnosticsData[]>([])
  const [thresholds, setThresholds] = useState<DiagnosticsThresholds>({})
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadDiagnosticsData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Query both tests and measurements with a join
      const { data: measurementData, error: measurementError } = await supabase
        .from('diagnostics_measurements')
        .select(`
          *,
          diagnostics_tests (
            id,
            user_id,
            filename,
            upload_date,
            test_date,
            test_type,
            test_protocol,
            created_at,
            updated_at
          )
        `)
        .eq('diagnostics_tests.user_id', athleteId)
        .order('time_seconds')

      if (measurementError) {
        throw new Error(`Failed to fetch diagnostics data: ${measurementError.message}`)
      }

      // Transform the joined data into the expected format
      const transformedData: DiagnosticsData[] = (measurementData || []).map((item: any) => ({
        id: item.id,
        user_id: item.diagnostics_tests.user_id,
        filename: item.diagnostics_tests.filename,
        upload_date: item.diagnostics_tests.upload_date,
        test_date: item.diagnostics_tests.test_date,
        test_type: item.diagnostics_tests.test_type,
        test_protocol: item.diagnostics_tests.test_protocol,
        time_seconds: item.time_seconds,

        // Copy all measurement fields
        breathing_frequency: item.breathing_frequency,
        tidal_volume: item.tidal_volume,
        minute_ventilation: item.minute_ventilation,
        vo2: item.vo2,
        vco2: item.vco2,
        respiratory_exchange_ratio: item.respiratory_exchange_ratio,
        ve_vo2_ratio: item.ve_vo2_ratio,
        ve_vco2_ratio: item.ve_vco2_ratio,
        mets: item.mets,
        energy_expenditure_hour: item.energy_expenditure_hour,
        energy_expenditure_minute: item.energy_expenditure_minute,
        heart_rate: item.heart_rate,
        feo2: item.feo2,
        feco2: item.feco2,
        feto2: item.feto2,
        fetco2: item.fetco2,
        fio2: item.fio2,
        fico2: item.fico2,
        peo2: item.peo2,
        peco2: item.peco2,
        peto2: item.peto2,
        petco2: item.petco2,
        treadmill_gradient: item.treadmill_gradient,
        speed_kmh: item.speed_kmh,
        pace_min_per_km: item.pace_min_per_km,
        test_phase: item.test_phase,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }))

      setData(transformedData)
    } catch (err) {
      console.error('Error loading diagnostics data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load diagnostics data')
    } finally {
      setLoading(false)
    }
  }, [athleteId])

  const loadDiagnosticsTests = useCallback(async () => {
    try {
      setError(null)

      const { data: testsData, error: testsError } = await supabase
        .from('diagnostics_tests')
        .select('*')
        .eq('user_id', athleteId)
        .order('test_date', { ascending: false })

      if (testsError) {
        throw new Error(`Failed to fetch diagnostics tests: ${testsError.message}`)
      }

      setTests(testsData || [])
    } catch (err) {
      console.error('Error loading diagnostics tests:', err)
      setError(err instanceof Error ? err.message : 'Failed to load diagnostics tests')
    }
  }, [athleteId])

  const uploadDiagnosticsFile = useCallback(
    async (file: File): Promise<void> => {
      try {
        setUploading(true)
        setError(null)

        // Validate file type
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
          throw new Error('Please upload an XLSX file')
        }

        // Read the XLSX file
        const arrayBuffer = await file.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })

        // Get the first worksheet
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]

        // Convert to JSON with header row
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: null,
        })

        if (jsonData.length < 2) {
          throw new Error('XLSX file must contain at least a header row and one data row')
        }

        // Extract headers and data
        const headers = jsonData[0] as string[]
        const dataRows = jsonData.slice(1)

        // Create a test record first
        const testData = {
          user_id: athleteId,
          filename: file.name,
          test_type: 'vo2_max',
          test_protocol: 'incremental',
          test_date: new Date().toISOString(),
        }

        const { data: testRecord, error: testError } = await supabase
          .from('diagnostics_tests')
          .insert(testData)
          .select()
          .single()

        if (testError) {
          throw new Error(`Failed to create test record: ${testError.message}`)
        }

        if (!testRecord) {
          throw new Error('No test record returned from database')
        }

        // Process each data row
        const measurements: any[] = []

        for (const row of dataRows) {
          if (!row || row.length === 0) continue

          const measurement: any = {
            test_id: testRecord.id,
            time_seconds: null,
          }

          // Map each column to database fields
          headers.forEach((header, index) => {
            const value = row[index]
            if (value === null || value === undefined) return

            const dbField = COLUMN_MAPPING[header]
            if (!dbField) return

            // console.log(`Mapping ${header} to ${dbField}: ${value}`)

            if (dbField === 'time') {
              measurement.time_seconds = typeof value === 'number' ? Math.floor(value * 24 * 60 * 60) : null
            } else if (dbField === 'pace_min_per_km') {
              // Store pace as interval string
              if (typeof value === 'number') {
                // 0.0021875 * 24 * 60 * 60
                const totalSeconds = Math.floor(value * 24 * 60 * 60)
                const seconds = totalSeconds % 60
                const minutes = Math.floor(totalSeconds / 60)
                console.log('pace_min_per_km:', value)
                measurement[dbField] =
                  `00:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
              }
            } else if (dbField === 'test_phase') {
              // Store as text
              measurement[dbField] = typeof value === 'string' ? value : String(value)
            } else {
              // Parse as numeric value
              measurement[dbField] = parseNumericValue(value)
            }
          })

          console.log('Measurement:', measurement)

          // Only add measurements that have at least time and one other measurement
          if (measurement.time_seconds && measurement.time_seconds >= 0 && Object.keys(measurement).length > 2) {
            measurements.push(measurement)
          }
        }

        if (measurements.length === 0) {
          throw new Error('No valid measurement data found in XLSX file')
        }

        console.log(`Uploading ${measurements.length} measurements for test ${testRecord.id}`)

        // Insert measurements in batches to avoid hitting size limits
        const batchSize = 100
        for (let i = 0; i < measurements.length; i += batchSize) {
          const batch = measurements.slice(i, i + batchSize)

          const { error: measurementError } = await supabase.from('diagnostics_measurements').insert(batch)

          if (measurementError) {
            // Clean up the test record if measurement insertion fails
            await supabase.from('diagnostics_tests').delete().eq('id', testRecord.id)

            throw new Error(`Failed to insert measurement data at index ${i}: ${measurementError.message}`)
          }
        }

        console.log(`Successfully uploaded ${measurements.length} measurements for test ${testRecord.id}`)

        // Reload data after successful upload
        await Promise.all([loadDiagnosticsData(), loadDiagnosticsTests()])
      } catch (error) {
        console.error('Error in uploadDiagnosticsFile:', error)
        throw error
      } finally {
        setUploading(false)
      }
    },
    [athleteId, loadDiagnosticsData, loadDiagnosticsTests]
  )

  const deleteDiagnosticsTest = useCallback(
    async (testId: string): Promise<void> => {
      try {
        setError(null)

        // Delete test (measurements will cascade delete)
        const { error } = await supabase.from('diagnostics_tests').delete().eq('id', testId)

        if (error) {
          throw new Error(`Failed to delete diagnostics test: ${error.message}`)
        }

        // Reload data after successful deletion
        await Promise.all([loadDiagnosticsData(), loadDiagnosticsTests()])
      } catch (error) {
        console.error('Error in deleteDiagnosticsTest:', error)
        setError(error instanceof Error ? error.message : 'Failed to delete diagnostics test')
        throw error
      }
    },
    [loadDiagnosticsData, loadDiagnosticsTests]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  useEffect(() => {
    // Sort data by time for analysis
    const sortedData = data
      .filter((d) => d.vo2 && d.vco2 && d.heart_rate && d.time_seconds !== null)
      .sort((a, b) => a.time_seconds - b.time_seconds)

    if (sortedData.length < 10) return

    // Calculate respiratory exchange ratio (RER = VCO2/VO2)
    const dataWithRER = sortedData.map((d) => ({
      ...d,
      rer: d.vco2! / d.vo2!,
    }))

    // VT1 Detection: First significant increase in VE/VO2 without increase in VE/VCO2
    let vt1Index = -1
    for (let i = 5; i < dataWithRER.length - 5; i++) {
      const prevWindow = dataWithRER.slice(i - 5, i)
      const nextWindow = dataWithRER.slice(i, i + 5)

      const prevVeVo2 = prevWindow.reduce((sum, d) => sum + (d.ve_vo2_ratio || 0), 0) / prevWindow.length
      const nextVeVo2 = nextWindow.reduce((sum, d) => sum + (d.ve_vo2_ratio || 0), 0) / nextWindow.length

      const prevVeVco2 = prevWindow.reduce((sum, d) => sum + (d.ve_vco2_ratio || 0), 0) / prevWindow.length
      const nextVeVco2 = nextWindow.reduce((sum, d) => sum + (d.ve_vco2_ratio || 0), 0) / nextWindow.length

      // VT1: Increase in VE/VO2 > 5% without significant increase in VE/VCO2
      if (nextVeVo2 > prevVeVo2 * 1.05 && nextVeVco2 < prevVeVco2 * 1.03) {
        vt1Index = i
        break
      }
    }

    // VT2 Detection: Second breakpoint where both VE/VO2 and VE/VCO2 increase
    let vt2Index = -1
    const startIndex = vt1Index > 0 ? vt1Index + 5 : Math.floor(dataWithRER.length * 0.4)

    for (let i = startIndex; i < dataWithRER.length - 5; i++) {
      const prevWindow = dataWithRER.slice(i - 5, i)
      const nextWindow = dataWithRER.slice(i, i + 5)

      const prevVeVco2 = prevWindow.reduce((sum, d) => sum + (d.ve_vco2_ratio || 0), 0) / prevWindow.length
      const nextVeVco2 = nextWindow.reduce((sum, d) => sum + (d.ve_vco2_ratio || 0), 0) / nextWindow.length

      // const _prevRER = prevWindow.reduce((sum, d) => sum + d.rer, 0) / prevWindow.length
      const nextRER = nextWindow.reduce((sum, d) => sum + d.rer, 0) / nextWindow.length

      // VT2: Increase in VE/VCO2 > 3% and RER approaching 1.0
      if (nextVeVco2 > prevVeVco2 * 1.03 && nextRER > 0.95) {
        vt2Index = i
        break
      }
    }

    setThresholds({
      vt1: vt1Index >= 0 ? dataWithRER[vt1Index] : undefined,
      vt2: vt2Index >= 0 ? dataWithRER[vt2Index] : undefined,
    })
  }, [data])

  // Calculate statistics from the data
  const diagnosticStats = useMemo(() => {
    if (data.length === 0) return null

    const athleteWeightKg = 70.0
    // biome-ignore lint/style/noNonNullAssertion: filter does check for undefined
    const vo2Values: number[] = data.filter((d) => d.vo2).map((d) => d.vo2!)
    const maxVO2 = Math.max(...vo2Values)
    const avgVO2 = vo2Values.reduce((sum, val) => sum + val, 0) / vo2Values.length
    // biome-ignore lint/style/noNonNullAssertion: filter does check for undefined
    const maxHR: number[] = data.filter((d) => d.heart_rate).map((d) => d.heart_rate!)

    const testDates = [...new Set(data.map((d) => d.test_date).filter(Boolean))]
    const latestTest = testDates.sort().reverse()[0]

    return {
      weight: athleteWeightKg,
      VO2max: Math.round(maxVO2) / athleteWeightKg,
      maxVO2: Math.round(maxVO2),
      avgVO2: Math.round(avgVO2),
      maxHR: Math.max(...maxHR),
      vt1: thresholds.vt1,
      vt2: thresholds.vt2,
      totalTests: testDates.length,
      latestTest: latestTest ? new Date(latestTest).toLocaleDateString() : 'N/A',
    }
  }, [data, thresholds])

  return {
    // State
    data,
    diagnosticStats,
    tests,
    loading,
    uploading,
    error,

    // Actions
    loadDiagnosticsData,
    loadDiagnosticsTests,
    uploadDiagnosticsFile,
    deleteDiagnosticsTest,
    clearError,
  }
}
